import React from "react";
import { useNavigate } from "react-router-dom";
import { fetchSignupConsentOptions, logSignupConsents } from "../../api/consents";
import { signup } from "../../api/auth";
import "./SignupForm.css";

export default function SignupForm({ onClose, onSuccess, variant = "page" }) {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [consentOptions, setConsentOptions] = React.useState([]);
  const [consents, setConsents] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [consentsExpanded, setConsentsExpanded] = React.useState(false);

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await fetchSignupConsentOptions();
        if (cancel) return;
        const items = Array.isArray(data) ? data : [];
        setConsentOptions(items);
        setConsents((prev) => {
          const next = { ...prev };
          for (const c of items) {
            if (!(c.id in next)) next[c.id] = !!c.required_for_service;
          }
          return next;
        });
      } catch (e) {
        if (!cancel) setError(e.message || "Failed to load consents");
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const toggleConsent = (id) => (e) => {
    const checked = e.target.checked;
    setConsents((prev) => ({ ...prev, [id]: checked }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload = {
        email,
        password,
        first_name: firstName?.trim() || undefined,
        last_name: lastName?.trim() || undefined,
      };

      const authResponse = await signup(payload);

      if (authResponse?.user_id && consentOptions.length > 0) {
        const decisions = consentOptions.map((opt) => ({
          consent_type_id: opt.id,
          consent_given: !!consents[opt.id],
        }));

        if (decisions.length > 0) {
          try {
            await logSignupConsents({ user_id: authResponse.user_id, decisions });
          } catch (consentErr) {
            console.error("Failed to log consents", consentErr);
          }
        }
      }

      if (onSuccess) {
        onSuccess(authResponse);
      } else {
        navigate('/signup/thank-you');
      }

      if (onClose) onClose();
    } catch (e) {
      setError(e.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const formClass = [
    "signup-form",
    `signup-form--${variant}`,
    variant === "modal" ? "form-vert-stack" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const showIntro = variant !== "modal";
  const requiredConsents = consentOptions.filter((opt) => opt.required_for_service);
  const optionalConsents = consentOptions.filter((opt) => !opt.required_for_service);
  const consentSummary = [
    requiredConsents.length ? `${requiredConsents.length} required` : null,
    optionalConsents.length ? `${optionalConsents.length} optional` : null,
  ]
    .filter(Boolean)
    .join(" • ") || 'No consents required';

  const renderMarkdown = React.useCallback((md) => {
    if (!md) return null;

    const escapeHtml = (str) => str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const formatInline = (text) => {
      let escaped = escapeHtml(text);
      escaped = escaped.replace(/`([^`]+)`/g, '<code>$1</code>');
      escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      escaped = escaped.replace(/__([^_]+)__/g, '<strong>$1</strong>');
      escaped = escaped.replace(/(?<!\*)\*([^*]+)\*/g, '<em>$1</em>');
      escaped = escaped.replace(/_([^_]+)_/g, '<em>$1</em>');
      escaped = escaped.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
      return escaped;
    };

    const renderBlock = (block) => {
      const lines = block.trim().split(/\n/);
      if (!lines.length) return '';

      const headingMatch = lines[0].match(/^(#{1,6})\s+(.*)$/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const content = formatInline(headingMatch[2]);
        const rest = lines.slice(1).join(' ').trim();
        const extra = rest ? `<p>${formatInline(rest)}</p>` : '';
        return `<h${level}>${content}</h${level}>${extra}`;
      }

      const unordered = lines.every((line) => /^[-*]\s+/.test(line));
      if (unordered) {
        const items = lines.map((line) => {
          const value = line.replace(/^[-*]\s+/, '');
          return `<li>${formatInline(value)}</li>`;
        });
        return `<ul>${items.join('')}</ul>`;
      }

      const ordered = lines.every((line) => /^\d+\.\s+/.test(line));
      if (ordered) {
        const items = lines.map((line) => {
          const value = line.replace(/^\d+\.\s+/, '');
          return `<li>${formatInline(value)}</li>`;
        });
        return `<ol>${items.join('')}</ol>`;
      }

      return `<p>${formatInline(lines.join(' '))}</p>`;
    };

    const normalized = md.replace(/\r\n/g, '\n').trim();
    if (!normalized) return null;
    const blocks = normalized.split(/\n{2,}/);
    const html = blocks.map(renderBlock).join('');
    return { __html: html };
  }, []);

  return (
    <form onSubmit={onSubmit} className={formClass}>
      {error && <div className="form-error">{error}</div>}

      {showIntro && (
        <div className="signup-form__intro">
          <h2>Create your ChicLogic.io account</h2>
          <p>Kick off your personalized hashtag strategy in under a minute.</p>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Email</label>
        <input
          className="form-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          autoComplete="email"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">First Name (optional)</label>
          <input
            className="form-input"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Last Name (optional)</label>
          <input
            className="form-input"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Password</label>
        <input
          className="form-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
        />
      </div>

      {consentOptions.length > 0 && (
        <div className="signup-form__consents">
          <button
            type="button"
            className="consents-toggle"
            onClick={() => setConsentsExpanded((v) => !v)}
            aria-expanded={consentsExpanded}
            aria-controls="signup-consents-panel"
          >
            <span className="consents-toggle__label">Review terms & consents</span>
            <span className="consents-toggle__summary">{consentSummary}</span>
            <span className={`consents-toggle__chevron ${consentsExpanded ? 'is-open' : ''}`} aria-hidden="true">▾</span>
          </button>

          {consentsExpanded && (
            <fieldset className="form-fieldset" id="signup-consents-panel">
              <legend className="form-legend">Consents</legend>
              {consentOptions.map((opt) => {
                const isRequired = !!opt.required_for_service;
                return (
                  <label key={opt.id} className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={!!consents[opt.id]}
                      onChange={toggleConsent(opt.id)}
                      disabled={isRequired}
                    />
                    <span className="checkbox-copy">
                      <span className="checkbox-title">
                        {opt.name}
                        {isRequired ? " (required)" : ""}
                      </span>
                      {opt.description && (
                        <div
                          className="checkbox-desc"
                          dangerouslySetInnerHTML={renderMarkdown(opt.description)}
                        />
                      )}
                    </span>
                  </label>
                );
              })}
            </fieldset>
          )}
        </div>
      )}

      <div className="form-actions">
        <button type="submit" disabled={loading} className="btn btn-primary">
          {loading ? "Signing up..." : "Sign Up"}
        </button>
        {onClose && (
          <button type="button" onClick={onClose} className="btn btn-link">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
